import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../services/userService";
import { useMutation } from "@tanstack/react-query";
const FEATURES = [
  { icon: "fi-rr-box", text: "Manage packages, activities & more" },
  { icon: "fi-rr-calendar-check", text: "Bookings & availability in one place" },
  { icon: "fi-rr-money", text: "Clear payouts and revenue tracking" },
];

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (credentials) => {
      try {
        const response = await login(credentials);
        if (response.status === false) {
          throw new Error(response.message);
        }
        return response;
      } catch (err) {
        if (err.response?.status === 401) {
          const message = err.response?.data?.message?.toLowerCase() || "";
          if (message.includes("pending approval")) {
            navigate("/verification-pending");
            throw new Error(err.response.data.message);
          }
          if (message.includes("rejected") || message.includes("not approved")) {
            navigate("/account-rejected");
            throw new Error(err.response.data.message);
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      localStorage.setItem("token", data.data.token);
      localStorage.setItem("user", JSON.stringify(data.data.user));
      navigate("/dashboard");
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while logging in";

      if (
        !errorMessage.toLowerCase().includes("pending approval") &&
        !errorMessage.toLowerCase().includes("rejected") &&
        !errorMessage.toLowerCase().includes("not approved")
      ) {
        setError(errorMessage);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    mutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen flex bg-slate-50 font-sans text-slate-900">
      {/* Brand panel */}
      <div className="relative hidden lg:flex lg:w-[42%] flex-col justify-center overflow-hidden bg-gradient-to-br from-primary-700 via-primary-800 to-primary-950 px-12 py-16 xl:px-16">
        <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -right-12 h-64 w-64 rounded-full bg-teal-400/15 blur-3xl" />

        <div className="relative z-10 max-w-md">
          <img
            src="https://www.exploreworld.com/_next/image?url=%2Fexploreworld-logo.png&w=256&q=75"
            alt="Explore World"
            className="mb-12 h-7 w-auto brightness-0 invert opacity-95"
          />

          <p className="text-sm font-medium text-teal-200/90">Supplier Portal</p>
          <h1 className="mt-3 text-3xl xl:text-[2.75rem] font-bold leading-tight tracking-tight text-white">
            Grow your travel business
          </h1>
          <p className="mt-5 text-base leading-relaxed text-white/65">
            Manage listings, bookings, and payouts in one place.
          </p>

          <ul className="mt-10 space-y-4 border-t border-white/10 pt-10">
            {FEATURES.map((f) => (
              <li key={f.text} className="flex items-center gap-3 text-sm text-white/75">
                <i className={`fi ${f.icon} text-base text-teal-300/90`} />
                {f.text}
              </li>
            ))}
          </ul>
        </div>

        <p className="absolute bottom-8 left-12 xl:left-16 text-xs text-white/35">
          © {new Date().getFullYear()} Explore World
        </p>
      </div>

      {/* Sign-in */}
      <div className="flex flex-1 flex-col justify-center px-6 py-10 sm:px-10 lg:px-16">
        <div className="mx-auto w-full max-w-[400px]">
          <div className="mb-8 lg:hidden">
            <img
              src="https://www.exploreworld.com/_next/image?url=%2Fexploreworld-logo.png&w=256&q=75"
              alt="Explore World"
              className="h-7 w-auto"
            />
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm sm:p-10">
            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">Welcome back</h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Sign in to your supplier account
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-6 flex gap-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                <i className="fi fi-rr-exclamation mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email
                </label>
                <div className="relative">
                  <i className="fi fi-rr-envelope pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <a
                    href="#"
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <i className="fi fi-rr-lock pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fi ${showPassword ? "fi-rr-eye" : "fi-rr-eye-crossed"}`} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {mutation.isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Signing in…
                  </span>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-500">
              New supplier?{" "}
              <a
                href="/register"
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Create an account
              </a>
            </p>
          </div>

          <div className="mt-8 flex justify-center gap-6 text-xs text-slate-400">
            <a href="#" className="hover:text-slate-600">
              Terms
            </a>
            <a href="#" className="hover:text-slate-600">
              Privacy
            </a>
            <a href="#" className="hover:text-slate-600">
              Help
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
